'use client';

import { FormEvent, useState } from 'react';
import { InputText } from '@/components/input-text';
import { InputTextarea } from '@/components/input-textarea';
import { Button } from '@/components/button';
import { FigureEnvelopeCheck } from './figures';

const WEB3FORMS_ACCESS_KEY = '7dfc777f-65c4-4c0a-a519-009ff0f2315c';

type ValidationResult = { isValid: boolean; error?: string };

type FormFields = {
  fullName: string;
  email: string;
  message: string;
};

type FieldErrors = Partial<Record<keyof FormFields, string>>;

const validateFullName = (name: string): ValidationResult => {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return { isValid: false, error: 'Full name is required' };
  }

  const nameParts = trimmedName.split(/\s+/);
  if (nameParts.length < 2) {
    return { isValid: false, error: 'Please enter both first and last name' };
  }

  if (!nameParts.every(part => part.length >= 2)) {
    return {
      isValid: false,
      error: 'First and last name must be at least 2 characters each',
    };
  }

  if (!/^[a-zA-Z\s\-']+$/.test(trimmedName)) {
    return {
      isValid: false,
      error: 'Name can only contain letters, spaces, hyphens, and apostrophes',
    };
  }

  return { isValid: true };
};

const validateEmail = (email: string): ValidationResult => {
  const trimmedEmail = email.trim();

  if (!trimmedEmail) {
    return { isValid: false, error: 'Email is required' };
  }

  if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  return { isValid: true };
};

const validateMessage = (message: string): ValidationResult => {
  const trimmedMessage = message.trim();

  if (!trimmedMessage) {
    return { isValid: false, error: 'Project details are required' };
  }

  if (trimmedMessage.length < 10) {
    return { isValid: false, error: 'Please provide at least 10 characters' };
  }

  return { isValid: true };
};

const validateForm = (fields: FormFields): FieldErrors => {
  const errors: FieldErrors = {};

  const nameValidation = validateFullName(fields.fullName);
  if (!nameValidation.isValid) {
    errors.fullName = nameValidation.error;
  }

  const emailValidation = validateEmail(fields.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }

  const messageValidation = validateMessage(fields.message);
  if (!messageValidation.isValid) {
    errors.message = messageValidation.error;
  }

  return errors;
};

const initialFields: FormFields = {
  fullName: '',
  email: '',
  message: '',
};

export function FormContactWeb3forms() {
  const [fields, setFields] = useState<FormFields>(initialFields);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formSent, setFormSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field: keyof FormFields, value: string) => {
    setFields(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    const errors = validateForm(fields);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setSubmitting(true);

    const trimmedName = fields.fullName.trim();
    const trimmedEmail = fields.email.trim();
    const trimmedMessage = fields.message.trim();

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          from_name: trimmedName,
          name: trimmedName,
          email: trimmedEmail,
          subject: `Contact - RayLo: ${trimmedName}`,
          message: trimmedMessage,
          replyto: trimmedEmail,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setFormSent(true);
      } else {
        setFormError('Something went wrong. Please try again later.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormError('Something went wrong. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (formSent) {
    return (
      <div className="bg-cream-dark flex flex-col items-center justify-center p-8 sm:h-full lg:h-full dark:bg-white/5">
        <FigureEnvelopeCheck className="text-copper mx-auto size-48" />

        <h2 className="dark:text-ivory mt-8 text-center font-serif text-3xl tracking-tight lg:text-5xl">
          Message Received!
        </h2>

        <p className="mt-2 text-lg font-light lg:text-2xl">
          I’ll get back to you soon.
        </p>
      </div>
    );
  }

  return (
    <form
      className="relative grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-8"
      onSubmit={onSubmit}
      noValidate>
      <div className="group mb-2 sm:col-span-2">
        <InputText
          label="Full name"
          type="text"
          name="fullName"
          value={fields.fullName}
          onChange={e => updateField('fullName', e.target.value)}
          placeholder="e.g. Alex Johnson"
          required
          error={fieldErrors.fullName ?? null}
        />
      </div>

      <div className="group mb-2 sm:col-span-2">
        <InputText
          label="Email"
          type="email"
          name="email"
          value={fields.email}
          onChange={e => updateField('email', e.target.value)}
          placeholder="you@company.com"
          required
          error={fieldErrors.email ?? null}
        />
      </div>

      <div className="group mb-2 sm:col-span-2">
        <InputTextarea
          label="What's on your mind?"
          name="message"
          value={fields.message}
          rows={5}
          onChange={e => updateField('message', e.target.value)}
          placeholder="Your project, timeline, budget, location, etc."
          required
          error={fieldErrors.message ?? null}
        />
      </div>

      {formError && (
        <div className="sm:col-span-2">
          <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
        </div>
      )}

      <div className="text-right sm:col-span-2">
        <Button btn="dark" arrow loading={submitting} type="submit">
          {submitting ? 'Sending...' : 'Send'}
        </Button>
      </div>
    </form>
  );
}
